import React, { useEffect, useState, useCallback, useMemo } from "react";

import { Typography } from "@packages/ds";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import Error from "Components/Error";
import ModalConfirmLeaveForm from "Components/ModalConfirmLeaveForm";
import { Button, ButtonWrapper } from "ds/Button";
import Input from "ds/Input";
import useDecodedParams from "Hooks/useDecodedParams";
import useInput from "Hooks/useInput";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { capitalize } from "Libs/utils";
import { environmentsAsArraySelector } from "Reducers/environment";
import { reloadProject, gitProjectSelector } from "Reducers/project/project";
import { useAddDomain } from "Reducers/project/settings/domains/hooks/useAddDomain";
import { useAppDispatch } from "Store/hooks";

import * as S from "./DomainForm.styles";

interface DomainFormProps {
  cancellable?: boolean;
}

const DomainForm = ({ cancellable = false }: DomainFormProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { organizationId, projectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();

  const environments = useSelectorWithUrlParams(environmentsAsArraySelector);

  const [modal, setModal] = useState<{
    isOpen: boolean;
  }>({
    isOpen: false
  });

  const { inputs, handleInputChange } = useInput({
    name: ""
  });

  const project = useSelectorWithUrlParams(gitProjectSelector);
  const { error, status, handleSave } = useAddDomain(project);

  const mainEnvironment = useMemo(
    () => environments?.find(environment => environment.is_main),
    [environments]
  );

  const goBackToList = useCallback(() => {
    navigate(`/${organizationId}/${projectId}/-/settings/domains`);
  }, [organizationId, projectId, navigate]);

  const handleSubmit = e => {
    e?.preventDefault();
    if (!inputs.name) return;
    handleSave(inputs);
  };

  const handleCancel = () => {
    if (!!inputs.name && !modal.isOpen) {
      setModal({ isOpen: true });
    } else {
      goBackToList();
    }
  };

  useEffect(() => {
    if (!status || status !== "added") return;

    dispatch(reloadProject({ projectId }));

    goBackToList();
  }, [status, projectId, dispatch, goBackToList]);

  return (
    <>
      <S.FormWrapper
        aria-labelledby="edit-domain-heading"
        onSubmit={handleSubmit}
      >
        {error?.message && <Error>{error.message}</Error>}

        <Input
          name="name"
          analyticId="settings.domains.name"
          label={intl.formatMessage({ id: "settings.domains.name" })}
          placeholder={intl.formatMessage({
            id: "settings.domains.placeholder"
          })}
          onChange={evt => handleInputChange(evt)}
          required
          error={error?.original?.detail?.["name"]}
          data-testid="input-name"
          width="264px"
        />

        <S.EnvironmentContainer>
          <Typography.Label>
            {capitalize(intl.formatMessage({ id: "domains.environment" }))}
          </Typography.Label>
          <S.Text>{mainEnvironment?.id}</S.Text>
        </S.EnvironmentContainer>

        <ButtonWrapper>
          {status === "pending" ? (
            <S.Loading />
          ) : (
            <>
              <Button
                analyticId="settings.domains.save.button"
                type="button"
                variant="primary"
                aria-label={intl.formatMessage({
                  id: "settings.domains.save.button"
                })}
                onClick={handleSubmit}
                disabled={!inputs.name}
                data-testid="save-domain-btn"
              >
                {capitalize(
                  intl.formatMessage({
                    id: "settings.domains.save.button"
                  })
                )}
              </Button>

              {cancellable && (
                <Button
                  type="button"
                  analyticId="cancel"
                  variant="secondary"
                  aria-label={intl.formatMessage({ id: "cancel" })}
                  onClick={handleCancel}
                  data-testid="cancel-domain-btn"
                >
                  {capitalize(intl.formatMessage({ id: "cancel" }))}
                </Button>
              )}
            </>
          )}
        </ButtonWrapper>
      </S.FormWrapper>

      {modal.isOpen && (
        <ModalConfirmLeaveForm
          isOpen={modal.isOpen}
          closeModal={() => setModal({ isOpen: false })}
          cancelFunction={() => setModal({ isOpen: false })}
          continueFunction={() => goBackToList()}
        />
      )}
    </>
  );
};

export default DomainForm;
